import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { X, MapPin, IndianRupee, User, Calendar } from "lucide-react";

const formatAmt = (n) =>
  n ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "₹0";

const formatDate = (d) => {
  if (!d) return "";
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
};

export default function BookingCelebrationModal({ bookings = [], onClose }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const latest = bookings[0];

  useEffect(() => {
    if (!latest) return;
    // Fire confetti burst
    const fire = (particleRatio, opts) =>
      confetti({
        ...opts,
        origin: { y: 0.6 },
        particleCount: Math.floor(200 * particleRatio),
      });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, [latest]);

  if (!latest) return null;

  const nightsLabel = latest.nights ? `${latest.nights}N ` : "";
  const destLabel = `${nightsLabel}${latest.destination || ""}`;

  const handleViewBookings = () => {
    onClose();
    navigate("/admin/bookings");
  };

  return (
    <div
      data-testid="booking-celebration-modal"
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden animate-scale-in">
        {/* Green celebration header */}
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 px-6 pt-8 pb-6 text-center">
          <div className="text-5xl mb-3" role="img" aria-label="celebration">
            <span className="inline-block animate-bounce">&#127881;</span>
          </div>
          <h2 className="text-white text-2xl font-bold tracking-tight">
            New Booking!
          </h2>
          <p className="text-emerald-100 text-sm mt-1">
            {bookings.length > 1
              ? `${bookings.length} new bookings received`
              : "A new booking has been confirmed"}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="celebration-modal-close"
          className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Booking details */}
        <div className="px-6 py-5 space-y-3">
          {destLabel && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Destination</p>
                <p className="text-gray-800 font-semibold text-base">{destLabel}</p>
              </div>
            </div>
          )}

          {latest.total_amount > 0 && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                <IndianRupee className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Booking Amount</p>
                <p className="text-gray-800 font-bold text-lg text-emerald-600">{formatAmt(latest.total_amount)}</p>
              </div>
            </div>
          )}

          {latest.client_name && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Guest</p>
                <p className="text-gray-800 font-medium">{latest.client_name}</p>
              </div>
            </div>
          )}

          {latest.travel_date && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Travel Date</p>
                <p className="text-gray-800 font-medium">
                  {formatDate(latest.travel_date)}
                  {latest.return_date ? ` → ${formatDate(latest.return_date)}` : ""}
                </p>
              </div>
            </div>
          )}

          {latest.agent_name && (
            <div className="mt-1 px-3 py-2 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-400">Confirmed by</p>
              <p className="text-gray-700 font-medium text-sm">{latest.agent_name}</p>
            </div>
          )}

          {bookings.length > 1 && (
            <p className="text-xs text-gray-400 text-center mt-1">
              +{bookings.length - 1} more booking{bookings.length - 1 > 1 ? "s" : ""} waiting
            </p>
          )}
        </div>

        {/* CTA buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={handleViewBookings}
            data-testid="celebration-view-bookings-btn"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl transition-colors text-sm"
          >
            View Bookings
          </button>
          <button
            onClick={onClose}
            data-testid="celebration-dismiss-btn"
            className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold py-2.5 rounded-xl transition-colors text-sm"
          >
            Dismiss
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scale-in {
          from { transform: scale(0.85); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-scale-in {
          animation: scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
    </div>
  );
}
